import React, { useRef, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import type { Project } from '../../types/portal';
import { projectStatus } from '../../utils/status';
import { removeCallSheet, uploadCallSheet } from '../../utils/callSheetStore';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD } from './adminStyles';

/** A small heading over a cluster of related fields — the everyday admin
    screens don't get Sign In's ceremony, but they can still tell you what
    you're looking at before you're three fields into it. */
function FieldGroup({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="mt-10 first:mt-0" data-id="element-296" data-mp="18dtfafsskmuz">
      <span className="label block text-paper/25" data-id="element-297" data-mp="1dcefif81n6jf">{label}</span>
      <div className="mt-5 grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-298" data-mp="1dbl7laxcmazp">{children}</div>
    </div>;
}
export function ProjectFields({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    activeProject,
    updateProject,
    notify
  } = usePortal();
  const [draft, setDraft] = useState<Project>(activeProject);
  const [callSheetBusy, setCallSheetBusy] = useState(false);
  const [callSheetFailed, setCallSheetFailed] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  function edit(patch: Partial<Project>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  async function uploadCallSheetFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setCallSheetBusy(true);
    setCallSheetFailed('');
    /* Stored in Supabase Storage before it is shown as attached — a blob URL
       only ever worked in the tab that created it. */
    const result = await uploadCallSheet(activeProject.id, file);
    setCallSheetBusy(false);
    if (!result.ok || !result.callSheet) {
      setCallSheetFailed(result.error ?? 'The call sheet could not be saved.');
      return;
    }
    const previous = draft.callSheet;
    edit({
      callSheet: result.callSheet
    });
    if (previous?.path) void removeCallSheet(previous.path);
  }
  function removeCallSheetFile() {
    const previous = draft.callSheet;
    edit({
      callSheet: undefined
    });
    setCallSheetFailed('');
    if (previous?.path) void removeCallSheet(previous.path);
  }
  function save(event: React.FormEvent) {
    event.preventDefault();
    updateProject({
      client: draft.client.trim() || activeProject.client,
      name: draft.name.trim() || activeProject.name,
      type: draft.type,
      description: draft.description,
      nextShootDate: draft.nextShootDate,
      nextShootTime: draft.nextShootTime,
      callSheet: draft.callSheet,
      location: draft.location,
      clientContact: draft.clientContact
      /* Portal responsibilities live in the Portal Team section (project_staff),
         so there is deliberately no producer field to type twice. */
    });
    notify('Project details updated');
    onClose();
  }
  return <form onSubmit={save} className="mt-10 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-299" data-mp="3ms10nmo3e0o9">
      <h3 className="font-display text-2xl leading-none" data-id="element-300" data-mp="rvaamh7ouou">Edit Project</h3>

      <div className="mt-8" data-id="element-301" data-mp="3rxwnb0kqlyd4">
        <FieldGroup label="Project" data-id="element-302" data-mp="3ryo77e4z27nk">
          <label className="block" data-id="element-303" data-mp="3rvjyq4kh4pr3">
            <span className="label block text-paper/40" data-id="element-304" data-mp="9jnewgjk19li">Client Name</span>
            <input value={draft.client} onChange={event => edit({
            client: event.target.value
          })} className={FIELD} data-id="element-305" data-mp="4sovh839rve1" />
          </label>
          <label className="block" data-id="element-306" data-mp="eigvihu51gpw">
            <span className="label block text-paper/40" data-id="element-307" data-mp="ej9wt3abve0n">Project Name</span>
            <input value={draft.name} onChange={event => edit({
            name: event.target.value
          })} className={FIELD} data-id="element-308" data-mp="shzp85m7cpio" />
          </label>
          <label className="block" data-id="element-309" data-mp="2u9mlalkybam0">
            <span className="label block text-paper/40" data-id="element-310" data-mp="2u8usfiiqnqzf">Project Type</span>
            <input value={draft.type} onChange={event => edit({
            type: event.target.value
          })} placeholder="e.g. Event Coverage" className={FIELD} data-id="element-311" data-mp="2q8atqaiiofb6" />
          </label>
          <div className="block" data-id="element-312" data-mp="2wmyhpfwagr1u">
            <span className="label block text-paper/40" data-id="element-313" data-mp="2wqdycn5m285d">Project Status</span>
            <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper" data-id="element-314" data-mp="30qeau295g99s">
              {projectStatus(activeProject)}
            </p>
            <span className="label-sm mt-3 block text-paper/30" data-id="element-315" data-mp="28zhzj62qmcom">
              Set automatically from the deliverables
            </span>
          </div>
        </FieldGroup>

        <FieldGroup label="Shoot" data-id="element-316" data-mp="2hzdcngfushra">
          <label className="block" data-id="element-317" data-mp="2hyc2rfakq1b5">
            <span className="label block text-paper/40" data-id="element-318" data-mp="2hxaf8bcdfmcj">Next Shoot Date</span>
            <input type="date" value={draft.nextShootDate} onChange={event => edit({
            nextShootDate: event.target.value
          })} className={FIELD} data-id="element-319" data-mp="2mrog2t9sylol" />
          </label>
          <label className="block" data-id="element-320" data-mp="1u7g3jx3sg3tv">
            <span className="label block text-paper/40" data-id="element-321" data-mp="1mqjm99ur7vvl">Next Shoot Time (optional)</span>
            <input value={draft.nextShootTime} onChange={event => edit({
            nextShootTime: event.target.value
          })} placeholder="e.g. 10:00 AM" className={FIELD} data-id="element-322" data-mp="1hqovd6azb6aj" />
          </label>
          <label className="block" data-id="element-323" data-mp="18pz5ej8ega4q">
            <span className="label block text-paper/40" data-id="element-324" data-mp="13rffk7qoqe73">Shoot Location (optional)</span>
            <input value={draft.location} onChange={event => edit({
            location: event.target.value
          })} placeholder="e.g. Portal Studio A · Al Quoz" className={FIELD} data-id="element-325" data-mp="13t8pbvwlhejb" />
          </label>
          <div className="block" data-id="element-326" data-mp="p3kosww8mjwn">
            <span className="label block text-paper/40" data-id="element-327" data-mp="p0gb98wvubtb">Call Sheet (optional)</span>
            {draft.callSheet ? <>
                <p className="mt-3 truncate border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper" data-id="element-328" data-mp="g1bd43p8gkho">
                  {draft.callSheet.fileName}
                </p>
                <div className="mt-3 flex items-center gap-6" data-id="element-329" data-mp="afkkcrwt0sxk">
                  <button type="button" onClick={() => fileInput.current?.click()} disabled={callSheetBusy} className="label text-paper/50 transition-colors duration-150 ease-out hover:text-paper" data-id="element-330" data-mp="ah5h8o6lcnyl">
                    {callSheetBusy ? 'Saving…' : 'Replace'}
                  </button>
                  <button type="button" onClick={removeCallSheetFile} disabled={callSheetBusy} className="label text-paper/35 transition-colors duration-150 ease-out hover:text-paper" data-id="element-331" data-mp="u3a4qe2evqwr">
                    Remove
                  </button>
                </div>
              </> : <button type="button" onClick={() => fileInput.current?.click()} disabled={callSheetBusy} className="label mt-3 w-full border border-paper/25 px-4 py-3 text-left text-paper/50 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-332" data-mp="1cv2wk55qv62w">
                {callSheetBusy ? 'Saving…' : 'Upload PDF'}
              </button>}
            {callSheetFailed ? <p role="alert" className="label-sm mt-3 text-paper/60" data-id="element-333" data-mp="2el2iokj9nsee">
                {callSheetFailed}
              </p> : null}
            <input ref={fileInput} type="file" accept="application/pdf,.pdf" onChange={uploadCallSheetFile} className="hidden" data-id="element-334" data-mp="29oltro48ta09" />
          </div>
        </FieldGroup>

        <FieldGroup label="Client Contact" data-id="element-335" data-mp="2vs2e8kjfi75">
          <label className="block" data-id="element-336" data-mp="2ugo3etgus1s">
            <span className="label block text-paper/40" data-id="element-337" data-mp="3u2med1gs6730">Main Client Contact</span>
            <input value={draft.clientContact} onChange={event => edit({
            clientContact: event.target.value
          })} placeholder="e.g. Sarah Jones · Marketing Director" className={FIELD} data-id="element-338" data-mp="3u4ywbemjghg2" />
          </label>
        </FieldGroup>

        <div className="mt-10" data-id="element-339" data-mp="qxwr1bet8fzn">
          <span className="label block text-paper/25" data-id="element-340" data-mp="vqhl8e1mov3p">Description</span>
          <label className="mt-5 block" data-id="element-341" data-mp="vskinj541qig">
            <span className="sr-only" data-id="element-342" data-mp="10qm1c2ymjnte">Project Description</span>
            <textarea value={draft.description} onChange={event => edit({
            description: event.target.value
          })} rows={3} placeholder="e.g. A three-part leadership series filmed at the client's Dubai office." className={`${FIELD} resize-none`} data-id="element-343" data-mp="10s6z01g8jmvh" />
          </label>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-344" data-mp="3cgy6bm7akj0u">
        <button type="submit" className={BUTTON_SOLID} data-id="element-345" data-mp="3gg6xqkev04t8">
          Save Project
        </button>
        <button type="button" onClick={onClose} className={BUTTON_QUIET} data-id="element-346" data-mp="38xpjk0w1g1tx">
          Cancel
        </button>
      </div>
    </form>;
}